import { useEffect, useState } from 'react';
import { t } from 'i18next';
import { useLocalStorage } from 'usehooks-ts';

import { GridIcon, ListIcon, ImageOutlinedIcon, SearchIcon, CloseIcon, PlayIcon, PauseIcon } from './Icons';

export const PageHeader = ({ title, description, actions, media, children }) => (
  <section className="rounded-xl bg-ms-raised p-6 dark:shadow-none shadow-sm">
    <div className="flex flex-col md:flex-row md:items-center gap-4">
      {media}
      <div className="flex-1 min-w-0">
        <h1 className="text-[22px] leading-tight font-medium text-ms-text truncate">{title}</h1>
        {description && (
          <div className="mt-2 text-sm text-ms-text leading-relaxed">{description}</div>
        )}
        {children}
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 md:justify-end md:max-w-[50%]">
          {actions}
        </div>
      )}
    </div>
  </section>
);

export const HeaderAction = ({ onClick, disabled, children, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`text-sm font-medium text-ms-accent hover:underline disabled:text-ms-muted disabled:no-underline disabled:cursor-default cursor-pointer ${className}`}
  >
    {children}
  </button>
);

export const useViewMode = (key) => useLocalStorage(`musicstyling.view.${key}`, 'grid');

export const useFullscreen = () => {
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggle = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      document.documentElement.requestFullscreen?.();
    }
  };

  return { isFullscreen, toggle, supported: !!document.fullscreenEnabled };
};

export const ViewToggle = ({ value, onChange }) => (
  <div className="flex items-center gap-1">
    {[
      { mode: 'grid', Icon: GridIcon, label: t('musicstyling.view.grid') },
      { mode: 'list', Icon: ListIcon, label: t('musicstyling.view.list') }
    ].map(({ mode, Icon, label }) => (
      <button
        key={mode}
        type="button"
        title={label}
        aria-label={label}
        aria-pressed={value === mode}
        onClick={() => onChange(mode)}
        className={`p-1.5 rounded-full cursor-pointer hover:bg-ms-raised ${value === mode ? 'text-ms-accent' : 'text-ms-text'}`}
      >
        <Icon className="w-6 h-6" />
      </button>
    ))}
  </div>
);

export const fieldClassName = 'h-11 rounded bg-transparent border border-ms-outline text-sm text-ms-text placeholder:text-ms-muted focus:outline-none focus:border-ms-accent focus:ring-1 focus:ring-ms-accent';

export const SearchInput = ({ value, onChange, placeholder, className = '' }) => (
  <div className={`relative ${className}`}>
    <SearchIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-ms-muted pointer-events-none" />
    <input
      type="search"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full pl-10 pr-10 ${fieldClassName} [&::-webkit-search-cancel-button]:hidden`}
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange('')}
        aria-label={t('player.filter.clear')}
        title={t('player.filter.clear')}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-ms-muted hover:text-ms-text cursor-pointer"
      >
        <CloseIcon className="w-4 h-4" />
      </button>
    )}
  </div>
);

export const FilterChips = ({ options, value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(option => (
      <button
        key={option.value}
        type="button"
        onClick={() => onChange(option.value)}
        className={`h-8 px-4 rounded-full text-sm font-medium border cursor-pointer transition-colors ${value === option.value
          ? 'bg-ms-accent-container text-ms-on-accent-container border-transparent'
          : 'bg-transparent text-ms-text border-ms-outline hover:bg-ms-raised'}`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

export const Pill = ({ children, className = '' }) => (
  <span className={`inline-block rounded-full bg-ms-accent-container text-ms-on-accent-container px-3 py-0.5 text-xs font-medium ${className}`}>
    {children}
  </span>
);

export const Cover = ({ src, alt = '', className = '', icon: Icon = ImageOutlinedIcon }) => (
  src ? (
    <img loading="lazy" src={src} alt={alt} className={`object-cover object-center bg-ms-raised ${className}`} />
  ) : (
    <div className={`flex items-center justify-center bg-ms-raised text-ms-outline ${className}`}>
      <Icon className="w-1/3 h-1/3 max-w-12 max-h-12" />
    </div>
  )
);

export const Equalizer = ({ paused = false, className = 'h-3.5' }) => (
  <span className={`ms-eq inline-flex items-end gap-[2px] ${paused ? 'paused' : ''} ${className}`} aria-hidden="true">
    <span /><span /><span />
  </span>
);

export const IconButton = ({ onClick, title, disabled, active, className = '', children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    disabled={disabled}
    className={`inline-flex items-center justify-center rounded-full p-2 cursor-pointer transition-colors disabled:cursor-default disabled:opacity-40 ${active ? 'text-ms-accent' : 'text-ms-text hover:bg-ms-raised'} ${className}`}
  >
    {children}
  </button>
);

export const PlayButton = ({ playing, onClick, disabled, title, size = 'sm' }) => {
  const sizeClass = size === 'lg' ? 'w-11 h-11' : 'w-7 h-7';
  const iconClass = size === 'lg' ? 'w-6 h-6' : 'w-4 h-4';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={`${sizeClass} shrink-0 inline-flex items-center justify-center rounded-full cursor-pointer transition-transform hover:scale-105 disabled:cursor-default disabled:opacity-40 disabled:hover:scale-100 ${playing ? 'bg-ms-accent text-ms-on-accent' : 'bg-ms-text text-ms-bg'}`}
    >
      {playing
        ? <PauseIcon className={iconClass} />
        : <PlayIcon className={`${iconClass} ml-0.5`} />}
    </button>
  );
};

export const EmptyState = ({ children }) => (
  <div className="col-span-full flex justify-center items-center h-40 rounded-xl border border-dashed border-ms-outline text-ms-muted text-sm">
    {children}
  </div>
);

export const Overlay = ({ icon: Icon, title, children }) => (
  <div className="absolute inset-0 z-100 flex items-center justify-center bg-ms-bg/70 backdrop-blur-[2px] p-6">
    <div className="flex flex-col items-center text-center gap-3 max-w-sm rounded-xl bg-ms-surface border border-ms-border px-8 py-7 shadow-2xl shadow-black/30">
      {Icon && (
        <span className="w-12 h-12 rounded-full bg-ms-danger/15 text-ms-danger inline-flex items-center justify-center">
          <Icon className="w-6 h-6" />
        </span>
      )}
      {title && <h3 className="text-base font-medium text-ms-text">{title}</h3>}
      <p className="text-sm text-ms-muted leading-relaxed">{children}</p>
    </div>
  </div>
);
